// src/components/ModifierSelector.js — bilingual
import React, { useState, useEffect, useMemo } from 'react';
import { useLanguage } from '../context/LanguageContext';
import strings         from '../i18n/translations';
import api             from '../services/api';

const isMulti = (group) => group.displayType !== 'radio';
const groupMax = (group) => (!group.maxSelect || group.maxSelect >= 99) ? Infinity : group.maxSelect;

// preloadedGroups: optional — when provided (e.g. a BundleComponent's own
// modifier groups, which have no standalone /menu/:id/modifiers endpoint),
// skips the API fetch and uses these directly. Simple-item flow (no prop
// passed) is unaffected — it still fetches from the menu item endpoint.
// closeOnAdd: default true (normal simple-item flow closes the modal after
// adding). BundleConfigurator sets this false so adding one component just
// hands data back to the parent step-through instead of closing everything.
// hideHeader: lets an embedding parent (BundleConfigurator) supply its own
// item/step header instead of stacking two headers in a cramped space.
export default function ModifierSelector({ item, onAdd, onClose, preloadedGroups, closeOnAdd = true, hideHeader = false, submitLabel = null }) {
  const { t, isAr, itemName } = useLanguage();
  const [groups,     setGroups]     = useState(preloadedGroups || []);
  const [selections, setSelections] = useState({});
  const [loading,    setLoading]    = useState(!preloadedGroups);
  const [step,       setStep]       = useState(0);
  const autoAdded = React.useRef(false);

  useEffect(() => {
    const applyGroups = (g) => {
      setGroups(g);
      const defaults = {};
      g.forEach(grp => {
        const def = grp.options?.find(o => o.isDefault);
        if (def) defaults[grp.id] = isMulti(grp) ? [def.id] : def.id;
      });
      setSelections(defaults);
      setLoading(false);
    };

    if (preloadedGroups) { applyGroups(preloadedGroups); return; }

    api.get(`/menu/${item.id}/modifiers`)
      .then(r => applyGroups(r.data || []))
      .catch(() => setLoading(false));
  }, [item.id, preloadedGroups]);

  const visibleGroups = useMemo(() => groups.filter(g => {
    if (!g.dependsOnGroupId) return true;
    const ps = selections[g.dependsOnGroupId];
    if (!ps) return false;
    if (!g.dependsOnOptionId) return !!ps;
    return Array.isArray(ps) ? ps.includes(g.dependsOnOptionId) : ps === g.dependsOnOptionId;
  }), [groups, selections]);

  const totalPrice = useMemo(() => {
    let price = item.price;
    visibleGroups.forEach(g => {
      const sel = selections[g.id];
      if (!sel) return;
      (Array.isArray(sel) ? sel : [sel]).forEach(id => {
        const opt = g.options?.find(o => o.id === id);
        if (opt) price += (opt.priceAdjustment || 0);
      });
    });
    return Math.max(0, price);
  }, [selections, visibleGroups, item.price]);

  const isValid = useMemo(() => visibleGroups.every(g => {
    if (!g.required) return true;
    const sel = selections[g.id];
    if (!sel) return false;
    return (Array.isArray(sel) ? sel : [sel]).length >= g.minSelect;
  }), [visibleGroups, selections]);

  const buildSummary = () => {
    const parts = [];
    visibleGroups.forEach(g => {
      const sel = selections[g.id];
      if (!sel) return;
      const ids   = Array.isArray(sel) ? sel : [sel];
      const names = ids.map(id => {
        const opt = g.options?.find(o => o.id === id);
        return isAr && opt?.nameAr ? opt.nameAr : opt?.name;
      }).filter(Boolean);
      if (names.length) {
        const gName = isAr && g.nameAr ? g.nameAr : g.name;
        parts.push(`${gName}: ${names.join(', ')}`);
      }
    });
    return parts.join(' | ');
  };

  const handleSelect = (group, optionId) => {
    setSelections(prev => {
      if (isMulti(group)) {
        const cur = prev[group.id] || [];
        const max = groupMax(group);
        if (cur.includes(optionId)) return { ...prev, [group.id]: cur.filter(id => id !== optionId) };
        if (cur.length >= max) return { ...prev, [group.id]: [...cur.slice(1), optionId] };
        return { ...prev, [group.id]: [...cur, optionId] };
      }
      const ns = { ...prev, [group.id]: optionId };
      groups.forEach(g => { if (g.dependsOnGroupId === group.id) delete ns[g.id]; });
      return ns;
    });
  };

  const handleAdd = () => {
    if (!isValid) return;
    onAdd({
      ...item,
      price:        totalPrice,
      basePrice:    item.price,
      selections,
      modifierNote: buildSummary(),
    });
    if (closeOnAdd) onClose();
  };

  if (loading) return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.8)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1500 }}>
      <div style={{ color:'var(--text2)' }}>{t(strings.loading)}</div>
    </div>
  );

  if (!loading && groups.length === 0) {
    if (!autoAdded.current) {
      autoAdded.current = true;
      onAdd({ ...item, price: item.price, basePrice: item.price, selections: {}, modifierNote: '' });
      if (closeOnAdd) onClose();
    }
    return null;
  }

  const currentGroup = visibleGroups[step];

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.85)', zIndex:1500, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
      <div style={{ background:'var(--bg2)', borderRadius:20, width:'100%', maxWidth:560, border:'1px solid var(--border)', maxHeight:'90vh', display:'flex', flexDirection:'column', direction: isAr ? 'rtl' : 'ltr' }}>

        {/* Item header */}
        {!hideHeader && (
          <div style={{ padding:'18px 22px', borderBottom:'1px solid var(--border)', display:'flex', alignItems:'center', gap:14 }}>
            <span style={{ fontSize:40 }}>{item.emoji}</span>
            <div>
              <div style={{ fontWeight:700, fontSize:16 }}>{itemName(item)}</div>
              <div style={{ fontSize:13, color:'var(--text2)' }}>{t(strings.customizeOrder)}</div>
            </div>
            <button onClick={onClose} style={{ marginLeft: isAr ? 0 : 'auto', marginRight: isAr ? 'auto' : 0, background:'none', border:'none', color:'var(--text3)', fontSize:22, cursor:'pointer' }}>✕</button>
          </div>
        )}

        {/* Step progress */}
        <div style={{ padding:'12px 22px', borderBottom:'1px solid var(--border)', display:'flex', gap:6, overflowX:'auto' }}>
          {visibleGroups.map((g, i) => {
            const sel  = selections[g.id];
            const done = Array.isArray(sel) ? sel.length > 0 : !!sel;
            const gName = isAr && g.nameAr ? g.nameAr : g.name;
            return (
              <div key={g.id} onClick={() => setStep(i)}
                style={{ display:'flex', alignItems:'center', gap:4, padding:'5px 12px', borderRadius:20, cursor:'pointer', whiteSpace:'nowrap', fontSize:12, fontWeight:600,
                  background: i===step ? 'var(--accent)' : done ? '#1a3d2a' : 'var(--bg3)',
                  color:      i===step ? '#000' : done ? 'var(--green)' : 'var(--text3)',
                  border:     `1px solid ${i===step ? 'var(--accent)' : done ? 'var(--green)' : 'var(--border)'}`,
                }}>
                {done && i !== step && '✓ '}{gName}
                {g.required && !done && <span style={{ color:'var(--red)', marginLeft:2 }}>*</span>}
              </div>
            );
          })}
        </div>

        {/* Current step */}
        {currentGroup && (
          <div style={{ flex:1, overflowY:'auto', padding:'16px 22px' }}>
            <div style={{ fontWeight:700, fontSize:15, marginBottom:4 }}>
              {isAr && currentGroup.nameAr ? currentGroup.nameAr : currentGroup.name}
              {currentGroup.required && <span style={{ color:'var(--red)', marginLeft:4 }}>*</span>}
            </div>
            <div style={{ fontSize:12, color:'var(--text2)', marginBottom:14 }}>
              {isMulti(currentGroup)
                ? `${t(strings.chooseUpTo)} ${groupMax(currentGroup) === Infinity ? '∞' : currentGroup.maxSelect}`
                : t(strings.chooseOne)}
              {currentGroup.required ? ' ' + t(strings.required) : ' ' + t(strings.optional)}
            </div>
            <div style={{ display:'grid', gridTemplateColumns: currentGroup.options?.length > 4 ? '1fr 1fr' : '1fr', gap:8 }}>
              {currentGroup.options?.map(opt => {
                const sel        = selections[currentGroup.id];
                const isSelected = Array.isArray(sel) ? sel.includes(opt.id) : sel === opt.id;
                const optName    = isAr && opt.nameAr ? opt.nameAr : opt.name;
                return (
                  <div key={opt.id} onClick={() => handleSelect(currentGroup, opt.id)}
                    style={{ display:'flex', alignItems:'center', gap:10, padding:'12px 14px', borderRadius:10, cursor:'pointer',
                      border:`2px solid ${isSelected ? 'var(--accent)' : 'var(--border)'}`,
                      background: isSelected ? '#3d2e10' : 'var(--bg3)', transition:'all .15s',
                    }}>
                    {opt.imageUrl && <img src={opt.imageUrl} alt="" style={{ width:40, height:40, borderRadius:8, objectFit:'cover' }} onError={e=>e.target.style.display='none'} />}
                    <div style={{ flex:1 }}>
                      <div style={{ fontWeight:600, fontSize:13, color: isSelected ? 'var(--accent)' : 'var(--text)' }}>{optName}</div>
                    </div>
                    <div style={{ fontWeight:700, fontSize:13, color: opt.priceAdjustment > 0 ? 'var(--green)' : 'var(--text2)', whiteSpace:'nowrap' }}>
                      {opt.priceAdjustment > 0 ? `+AED ${opt.priceAdjustment}` : opt.priceAdjustment < 0 ? `-AED ${Math.abs(opt.priceAdjustment)}` : t(strings.free)}
                    </div>
                    {isSelected && <div style={{ width:18, height:18, borderRadius: isMulti(currentGroup) ? 4 : '50%', background:'var(--accent)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, color:'var(--button-text)', fontWeight:700 }}>✓</div>}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Footer */}
        <div style={{ padding:'16px 22px', borderTop:'1px solid var(--border)', background:'var(--bg3)' }}>
          {buildSummary() && <div style={{ fontSize:12, color:'var(--text2)', marginBottom:10, lineHeight:1.5 }}>{buildSummary()}</div>}
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:12 }}>
            <div style={{ display:'flex', gap:8 }}>
              {step > 0 && <button className="btn btn-ghost btn-sm" onClick={() => setStep(s => s-1)}>{t(strings.back)}</button>}
              {step < visibleGroups.length - 1 && <button className="btn btn-ghost btn-sm" onClick={() => setStep(s => s+1)}>{t(strings.next)}</button>}
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:12 }}>
              <div style={{ textAlign: isAr ? 'left' : 'right' }}>
                <div style={{ fontSize:11, color:'var(--text3)' }}>{t(strings.total)}</div>
                <div style={{ fontSize:20, fontWeight:700, color:'var(--accent)' }}>AED {totalPrice.toFixed(2)}</div>
              </div>
              <button onClick={handleAdd} disabled={!isValid}
                style={{ padding:'12px 24px', borderRadius:10, background: isValid ? 'var(--accent)' : 'var(--bg4)', color: isValid ? '#000' : 'var(--text3)', fontWeight:700, fontSize:14, border:'none', cursor: isValid ? 'pointer' : 'not-allowed' }}>
                {isValid ? (submitLabel || t(strings.addToCartBtn)) : t(strings.completeRequired)}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
